import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  AlertTriangle,
  ArrowRightLeft,
  Briefcase,
} from "lucide-react";
import { opportunityAPI } from "@/services/opportunityService";
import { pipelineAPI } from "@/services/pipelineService";
import { useToast } from "@/hooks/use-toast";
import type { User } from "@/types/crm";
import { useAuth } from "@/hooks/useAuth";

interface ReassignModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "individual" | "bulk";
  currentOwner: User;
  targetUsers: User[];
  slug: string;
  opportunity?: { id: string; name: string; pipeline_id?: string };
  onSuccess: () => void;
}

export function ReassignModal({
  isOpen,
  onClose,
  type,
  currentOwner,
  targetUsers,
  slug: propSlug,
  opportunity,
  onSuccess,
}: ReassignModalProps) {
  const { toast } = useToast();
  const { company } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [opportunityCount, setOpportunityCount] = useState<number | null>(null);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [allowedRoleIds, setAllowedRoleIds] = useState<string[]>([]);

  // Recuperar slug del contexto si no viene en las props
  const slug = propSlug || company?.slug || "";

  useEffect(() => {
    const fetchPipelinePermissions = async () => {
      if (!slug || !opportunity?.pipeline_id || type !== "individual") {
        setAllowedRoleIds([]);
        return;
      }
      try {
        const response = await pipelineAPI.getPipelinePermissions(
          slug,
          opportunity.pipeline_id,
        );
        if (response.success && Array.isArray(response.data)) {
          const ids = response.data.map((p: any) => p.role_id);
          setAllowedRoleIds(ids);
        }
      } catch (error) {
        console.error("Error fetching pipeline permissions:", error);
      }
    };

    if (isOpen) {
      fetchPipelinePermissions();
    }
  }, [isOpen, slug, opportunity?.pipeline_id, type]);

  // Optimización: useMemo para evitar filtrar usuarios en cada render
  const availableUsers = useMemo(() => {
    return targetUsers.filter((u) => {
      if (u.id === currentOwner.id) return false;
      if (type === "individual" && opportunity?.pipeline_id) {
        if ((u as any).is_owner) return true;

        // Permitir siempre a administradores y super admins
        const roleName = (u as any).role?.name?.toLowerCase() || "";
        if (roleName.includes("admin") || roleName.includes("super"))
          return true;

        // Si no hay restricciones (array vacío), permitimos a todos
        if (allowedRoleIds.length === 0) return true;

        const userRoleId =
          (u as any).role_id || (u as any).roleId || (u as any).role?.id;

        const isAllowed = allowedRoleIds.includes(userRoleId);
        return isAllowed;
      }
      return true;
    });
  }, [
    targetUsers,
    currentOwner.id,
    type,
    opportunity?.pipeline_id,
    allowedRoleIds,
  ]);

  useEffect(() => {
    if (isOpen && type === "bulk" && currentOwner.id && slug) {
      setIsLoadingCount(true);
      const params = {
        slug,
        created_by: currentOwner.id,
        limit: 1, // We only need the total count from pagination
      };

      opportunityAPI
        .getOpportunities(params as any)
        .then((response) => {
          if (response.pagination) {
            setOpportunityCount(response.pagination.total);
          } else {
            setOpportunityCount(0);
          }
        })
        .catch((err) => {
          setOpportunityCount(0);
        })
        .finally(() => setIsLoadingCount(false));
    } else {
      setOpportunityCount(null);
    }
    setSelectedUserId("");
  }, [isOpen, type, currentOwner, slug]);

  const handleReassign = async () => {
    if (!selectedUserId) return;
    setIsSubmitting(true);

    try {
      if (type === "individual" && opportunity) {
        await opportunityAPI.reassignOpportunity(opportunity.id, {
          slug,
          new_owner_id: selectedUserId,
        });
        toast({
          title: "Éxito",
          description: "Cotización reasignada correctamente.",
        });
      } else {
        await opportunityAPI.bulkReassignOpportunities({
          slug,
          current_owner_id: currentOwner.id,
          new_owner_id: selectedUserId,
        });
        toast({
          title: "Éxito",
          description: "Cartera de cotizaciones transferida correctamente.",
        });
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo realizar la reasignación.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-blue-600" />
            {type === "individual"
              ? "Reasignar Cotización"
              : "Traspaso de Cartera"}
          </DialogTitle>
          <DialogDescription>
            Transfiere la responsabilidad{" "}
            {type === "individual"
              ? "de esta cotización"
              : "de todas las cotizaciones"}{" "}
            a otro usuario.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-md bg-slate-50 p-3 border border-slate-100">
            <div className="text-sm font-medium text-slate-500 mb-1">
              Propietario Actual
            </div>
            <div className="text-sm text-slate-900 font-semibold">
              {currentOwner.first_name} {currentOwner.last_name}
            </div>

            <div className="mt-3 pt-3 border-t border-slate-200">
              <div className="text-sm font-medium text-slate-500 mb-1">
                {type === "individual"
                  ? "Cotización a transferir"
                  : "Volumen a transferir"}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-900">
                {type === "individual" ? (
                  <span className="font-medium">{opportunity?.name}</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-slate-400" />
                    {isLoadingCount ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <span className="font-bold">{opportunityCount ?? 0}</span>
                    )}
                    cotizaciones activas
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Nuevo Propietario</Label>
            <Select
              value={selectedUserId}
              onValueChange={setSelectedUserId}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar usuario..." />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {type === "bulk" && (
              <p className="text-xs text-amber-600 flex items-center mt-2">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Esta acción moverá todas las cotizaciones al nuevo usuario.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleReassign}
            disabled={!selectedUserId || isSubmitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirmar Reasignación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
